import { ReactNode, useState } from "react";
import { X } from "lucide-react";
import { UUID } from "@bitory-gmbh/nidavellir";
import { toast } from "sonner";
import {
  joinFieldName,
  useErrValidationFailedContext,
} from "@/shared/lib/errors/err-validation-failed-context";
import t from "@/lang/lang";
import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import {
  CommonDialogFooter,
  CommonDialogHeader,
  CommonDialogTrigger,
} from "@/shared/components/dialog/dialog-common";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useHtzSummaryRemoveItemMutation } from "@/services/backend/htz/summary/api/summary-api";

/**
 * SummaryItemRemoveDialog removes an item from the summary. The Anlagenbericht
 * the item summarizes is kept and can be added again.
 *
 * The dialog is uncontrolled by default. Pass `open` and `onOpenChange` to
 * control it, e.g. when it is opened from a dropdown menu whose content
 * unmounts on close; no trigger is rendered then.
 */
export function SummaryItemRemoveDialog({
  summaryId,
  itemId,
  trigger,
  open: controlledOpen,
  onOpenChange,
  onRemoved,
}: {
  summaryId: UUID;
  itemId: UUID;
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onRemoved?: () => void;
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const setOpen = onOpenChange ?? setUncontrolledOpen;

  const [remove, { isLoading, error, reset }] =
    useHtzSummaryRemoveItemMutation();
  const { resetFields } = useErrValidationFailedContext();

  const onConfirm = () => {
    if (isLoading) {
      return;
    }

    remove({ id: summaryId, itemId })
      .unwrap()
      .then(() => {
        toast.success(t("Zeile entfernt"));
        resetFields(joinFieldName("items", itemId));
        reset();
        setOpen(false);
        onRemoved?.();
      })
      .catch(() => {
        // error is surfaced via RTKQueryErrorAlert; swallow the rejection
      });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isControlled &&
        (trigger || (
          <CommonDialogTrigger icon={X} label={t("Entfernen")} size="sm" />
        ))}
      <DialogContent>
        <CommonDialogHeader
          title={t("Zeile entfernen?")}
          description={t(
            "Die Zeile wird aus dem Inspektionsbericht entfernt. Der Anlagenbericht bleibt erhalten und kann erneut hinzugefügt werden. In der Zeile angepasste Werte gehen verloren.",
          )}
        />
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter
          isLoading={isLoading}
          onClickAction={onConfirm}
          actionLabel={t("Entfernen")}
        />
      </DialogContent>
    </Dialog>
  );
}
